import { quoteCallsCompleted } from './../quote/quote.action';
import { QuoteStatus } from '@app/core/models/api/dsm-types';
import { ProductType } from '@core/models/api/dsm-types';
import { getSelectedProducts } from './../product/product.selectors';
import { Injectable } from '@angular/core';
import { FullStoryService } from '@core/services/fullstory.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  map,
  switchMap,
  take,
  withLatestFrom,
  mergeMap,
  distinctUntilChanged,
  tap,
  debounceTime,
} from 'rxjs/operators';
import { setHubLoaded, updateRatingState } from './session.action';
import { ProductUtils } from '@shared/utils/product.util';
import { ProductActions } from '@core/store/actions';
import * as fromActions from '@core/store/actions/index';
import { from } from 'rxjs';
import { retrieveQuoteSuccess } from '@core/store/retrieve/retrieve.action';
import { selectAllTasks } from '@entities/task/selectors/task.selector';
import {
  BoatSupportedStates,
  RvSupportedStates,
  UmbrellaSupportedStates,
} from '@shared/constants/product-availability-constants';
import { SessionSelectors } from '@core/store/selectors';
import { AuthUrlService } from '@core/services/auth-url.service';
import { SessionService } from '@core/services/session.service';
import { UserService } from '@core/services/user.service';

@Injectable()
export class SessionEffects {
  private accessTokenRefreshTimeout: number | null = null;

  constructor(
    private actions$: Actions,
    private store: Store,
    private fullStoryService: FullStoryService,
    private window: Window,
    private authUrlService: AuthUrlService,
    private sessionService: SessionService,
    private userService: UserService
  ) {}

  logInitialHubPageStatistics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setHubLoaded),
        take(1),
        switchMap(() =>
          this.actions$.pipe(ofType(quoteCallsCompleted), take(1))
        ),
        withLatestFrom(
          this.store.select(getSelectedProducts),
          this.store.select(selectAllTasks)
        ),
        map(([_, selectedProducts, tasks]) => {
          const tasksCount: {
            [key in ProductType]?: { status: QuoteStatus; taskCount: number };
          } = selectedProducts.reduce((prev, next) => {
            return {
              ...prev,
              [next.type]: {
                status: next.quoteStatus || 'Draft',
                taskCount: 0,
              },
            };
          }, {});

          const selectedPropertyProduct = selectedProducts.find((product) =>
            ProductUtils.isPropertyProduct(product.type)
          )?.type;

          tasks.forEach((task) => {
            if (task.productType === 'All') {
              return;
            }

            let count: number;
            if (task.productType === 'Homeowner') {
              if (selectedPropertyProduct) {
                count = tasksCount[selectedPropertyProduct]?.taskCount || 0;
                tasksCount[selectedPropertyProduct] = {
                  status:
                    tasksCount[selectedPropertyProduct]?.status || 'Draft',
                  taskCount: count + 1,
                };
                return;
              } else {
                return;
              }
            }

            count = tasksCount[task.productType]?.taskCount || 0;
            tasksCount[task.productType] = {
              status: tasksCount[task.productType]?.status || 'Draft',
              taskCount: count + 1,
            };
          });

          Object.entries(tasksCount).forEach(([productType, count]) => {
            if (count) {
              this.fullStoryService.logHubLoadStatistics(
                productType as ProductType,
                count
              );
            }
          });
        })
      ),
    { dispatch: false }
  );

  updateRatingStateOnRetrieve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveQuoteSuccess),
      map((action) => action.payload.response),
      mergeMap((response) => {
        return from([
          fromActions.SessionActions.updateRatingState({
            payload: response.policyAddress?.state as string,
          }),
        ]);
      })
    )
  );

  setDsmActiveByRatingState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateRatingState),
      map((action) => action.payload),
      mergeMap((ratingState) => {
        let nextActions: Action[] = [];
        nextActions.push(
          ProductActions.updateProduct({
            payload: {
              id: 'Boat',
              changes: {
                isDsmActive: BoatSupportedStates.includes(ratingState),
                type: 'Boat',
              },
            },
          })
        );
        nextActions.push(
          ProductActions.updateProduct({
            payload: {
              id: 'RV',
              changes: {
                isDsmActive: RvSupportedStates.includes(ratingState),
                type: 'RV',
              },
            },
          })
        );
        nextActions.push(
          ProductActions.updateProduct({
            payload: {
              id: 'PersonalUmbrella',
              changes: {
                isDsmActive: UmbrellaSupportedStates.includes(ratingState),
                type: 'PersonalUmbrella',
              },
            },
          })
        );
        return from(nextActions);
      })
    )
  );

  refreshAccessTokenShortlyBeforeExpiry$ = createEffect(
    () =>
      this.store.select(SessionSelectors.getSessionState).pipe(
        distinctUntilChanged((a, b) => a.accessToken === b.accessToken),
        // Brief delay both to ensure no rapid-fire changes, and to give jwt a chance to populate.
        debounceTime(500),
        tap((session) => {
          this.scheduleAccessTokenRefresh(session.accessToken);
        })
      ),
    { dispatch: false }
  );

  private scheduleAccessTokenRefresh(accessToken: string | undefined): void {
    let jwt = null;
    try {
      jwt = JSON.parse(this.window.sessionStorage.getItem('jwt') || '');
    } catch (e) {}
    if (this.accessTokenRefreshTimeout) {
      this.window.clearTimeout(this.accessTokenRefreshTimeout);
      this.accessTokenRefreshTimeout = null;
    }
    let refreshToken = this.sessionService.fetchRefreshToken();
    if (jwt && accessToken && refreshToken) {
      const msTillExpiry = (jwt?.exp || 0) * 1000 - Date.now();
      const breathingRoomMs = 15 * 60 * 1000;
      const msTillRefresh = msTillExpiry - breathingRoomMs;
      if (msTillRefresh > 0) {
        this.accessTokenRefreshTimeout = this.window.setTimeout(() => {
          this.accessTokenRefreshTimeout = null;
          this.refreshAccessToken(refreshToken!);
        }, msTillRefresh);
      }
    }
  }

  private refreshAccessToken(refreshToken: string): void {
    this.authUrlService
      .exchangeRefreshToken(refreshToken)
      .pipe(take(1))
      .subscribe(({ accessToken, refreshToken }) => {
        if (accessToken && refreshToken) {
          this.sessionService.updateToken(accessToken, 'access_token');
          this.sessionService.updateToken(refreshToken, 'refresh_token');
          this.sessionService
            .fetchTokenIntrospectionWithAccessToken(accessToken)
            .pipe(take(1))
            .subscribe((tokenInfo) => {
              if (tokenInfo) {
                this.sessionService.storeJwt(tokenInfo);
                let jwt = null;
                try {
                  jwt = JSON.parse(
                    this.window.sessionStorage.getItem('jwt') || ''
                  );
                } catch (e) {}
                this.userService.populateJwtAndTokenIntrospectInfo(
                  jwt,
                  tokenInfo
                );
              }
            });
        }
      });
  }
}
